import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { srcSet, type ShopListItem } from '../api';
import { useCart } from '../cart';
import { discountPercent } from '../money';
import { useMoney } from '../currency';
import { Stars } from '../components/Stars';
import { useFavorites } from '../favorites';
import { isLowStock, isNew } from '../labels';
import { usePromotionLabels } from '../promotionLabels';
import { UnitPriceNote } from './UnitPrice';

/**
 * Dlazdice zbozi.
 *
 * Koupit jde primo odsud, ale jen u produktu s JEDINOU variantou. U zbozi
 * s vic balenimi by tlacitko muselo vybrat za zakaznika – takova dlazdice
 * proto posle na detail. Rozhoduje o tom `variantCount` ze serveru, ne
 * odhad ve vyloze.
 */
export function ProductCard({ product, index }: { product: ShopListItem; index: number }) {
  const { format } = useMoney();
  const cart = useCart();
  const favorites = useFavorites();
  const [added, setAdded] = useState(false);
  const isFavorite = favorites.has(product.id);

  // Casovac, ktery vraci tlacitko z „✓ V kosiku" zpet. Drzime si ho, aby
  // se dal zrusit pri odmontovani – jinak by po prechodu na jinou stranku
  // sahal na stav uz neexistujici dlazdice.
  const resetTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(resetTimer.current), []);

  const discount = discountPercent(product.priceFromMinor, product.compareAtPriceFromMinor);
  // Stitek akce – text (napr. „2+1 zdarma") chodi ze serveru. Fail-silent:
  // kdyz nedorazi, prvek se proste nevykresli.
  const promoLabels = usePromotionLabels(product.id);
  const buyable =
    product.inStock && product.variantCount === 1 && product.singleVariantId !== null;

  function addToCart(event: React.MouseEvent) {
    // Dlazdice je odkaz na detail; nakup z ni nesmi zaroven prejit jinam.
    event.preventDefault();
    event.stopPropagation();
    if (product.singleVariantId === null) return;

    cart.add(
      {
        variantId: product.singleVariantId,
        productName: product.name,
        variantName: product.singleVariantName ?? '',
        slug: product.slug,
        priceMinor: product.priceFromMinor,
        available: product.singleVariantAvailable,
      },
      1,
    );

    setAdded(true);
    window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => setAdded(false), 1600);
  }

  return (
    <Link
      to={`/produkt/${product.slug}`}
      className="card product-card"
      /* Poradi ve mrizce rizne zpozdeni nastupu – dlazdice nenaskoci
         naraz, ale postupne. Strop drzi cekani rozumne i u velkych
         katalogu. */
      style={{ '--i': Math.min(index, 11) } as React.CSSProperties}
    >
      <button
        type="button"
        className={isFavorite ? 'fav-heart is-on' : 'fav-heart'}
        aria-label={isFavorite ? `Odebrat ${product.name} z oblíbených` : `Přidat ${product.name} do oblíbených`}
        aria-pressed={isFavorite}
        onClick={(event) => {
          // Dlaždice je odkaz na detail; srdíčko nesmí zároveň navigovat.
          event.preventDefault();
          event.stopPropagation();
          favorites.toggle(product);
        }}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
          <path
            d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
            fill={isFavorite ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className="thumb" aria-hidden="true">
        {product.imageUrl === null ? (
          <span className="thumb-placeholder">{product.name.slice(0, 1)}</span>
        ) : (
          <img
            src={product.imageUrl}
            srcSet={srcSet(product.imageThumbnails)}
            /* Dlaždice je ve mřížce nejvýš ~300 px široká. Bez `sizes` by
               prohlížeč počítal se šířkou okna a stáhl zbytečně velkou
               variantu. */
            sizes="(max-width: 640px) 45vw, 260px"
            alt=""
            loading="lazy"
            decoding="async"
          />
        )}
        {isNew(product.createdAt) && <span className="tag new">Novinka</span>}
        {discount !== null && <span className="tag sale">−{discount} %</span>}
        {!product.inStock && <span className="tag out">Vyprodáno</span>}
        {product.inStock &&
          product.variantCount === 1 &&
          isLowStock(product.singleVariantAvailable) && (
            <span className="tag low">Poslední kusy</span>
          )}
        {/* Akcni stitek – barevne odliseny od cenoveho, aby „2+1 zdarma"
            nesplyvalo s preskrtnutou cenou. Text ze serveru. */}
        {promoLabels[0] !== undefined && (
          <span className="tag promo" title={promoLabels[0].name}>
            {promoLabels[0].badge}
          </span>
        )}
      </div>

      <div className="product-body">
        {product.brand !== null && <span className="brand-line">{product.brand}</span>}
        <strong className="product-name">{product.name}</strong>

        {product.ratingCount > 0 && (
          <Stars value={product.ratingAverage} count={product.ratingCount} />
        )}

        <div className="price-block">
          {product.compareAtPriceFromMinor !== null && (
            <s className="price-was">{format(product.compareAtPriceFromMinor)}</s>
          )}
          <span className={discount !== null ? 'price is-sale' : 'price'}>
            {product.priceFromMinor === product.priceToMinor
              ? format(product.priceFromMinor)
              : `od ${format(product.priceFromMinor)}`}
          </span>
          {/*
            CENA ZA MĚRNOU JEDNOTKU NA DLAŽDICI (#9).

            Na dlaždici PATŘÍ: srovnávat balení se dá jen ve výpisu, kde jsou
            vedle sebe — na detailu už si zákazník vybral. Server ji ale
            posílá jen u zboží s JEDINOU variantou, takže se nikdy neukáže
            u dlaždice s cenou „od …", kde by patřila k jinému balení.

            Výkon: server ji spočítal při skládání odpovědi, výloha jen čte
            hotové číslo. Žádný dotaz, žádný přepočet, žádný další render.
          */}
          <UnitPriceNote source={product} />
        </div>

        {buyable ? (
          <button
            type="button"
            className={added ? 'buy-button is-added' : 'buy-button'}
            onClick={addToCart}
          >
            {added ? (
              '✓ V košíku'
            ) : (
              <>
                <CartIcon />
                Do košíku
              </>
            )}
          </button>
        ) : (
          <span className="buy-button as-link">
            {product.inStock ? 'Vybrat variantu' : 'Zobrazit detail'}
          </span>
        )}
      </div>
    </Link>
  );
}

/** Ikonka košíku na tlačítku „Do košíku". */
function CartIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">
      <path
        d="M3 4h2l2.4 11.2a1.6 1.6 0 0 0 1.57 1.3h7.7a1.6 1.6 0 0 0 1.57-1.27L21 8H6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="20" r="1.4" fill="currentColor" />
      <circle cx="17" cy="20" r="1.4" fill="currentColor" />
    </svg>
  );
}
